import { usePermissions, useTranslate } from 'react-admin';
import { Box, Chip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { serviceRequestPriorityChoices, serviceRequestTypeChoices } from '@/components/global/options/selectOptions.jsx';
import CommentBubble from '@/components/global/fields/CommentBubble';
import { useDrawer } from '@/composables/useDrawer';

const StyledServiceRequestIdField = styled('div')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    alignItems: 'center',
    paddingBottom: '22px',
    [theme.breakpoints.up('lg')]: {
        flexDirection: 'column',
        flexWrap: 'nowrap',
        alignItems: 'flex-start',
        paddingBottom: '0px',
        borderBottom: 'none',
    },
    '& .service-request-id-field-id': {
        fontWeight: 600,
    },
    '& .service-request-id-field-chips': {
        display: 'flex',
        gap: '4px',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            flexWrap: 'wrap',
        },
    },
}));

export const ServiceRequestIdField = ({ record, resource }) => {
    const { permissions } = usePermissions();
    const translate = useTranslate();
    const { openDrawer } = useDrawer();

    if (!record) return null;

    const formattedId = record.customId.toString().padStart(6, '0');
    const priority = serviceRequestPriorityChoices(translate).find(s => s.id === record.priority);
    const type = serviceRequestTypeChoices(translate).find(s => s.id === record.type);
    const canSeeHiddenComments = Boolean(permissions?.isAdmin || permissions?.isServiceMaster || permissions?.isBok);

    const handleOpenActivities = event => {
        event.stopPropagation();
        openDrawer(record.id, `${resource}-edit`, 'activities');
    };

    return (
        <StyledServiceRequestIdField>
            <Typography className='service-request-id-field-id'>#{formattedId}</Typography>
            <Box className='service-request-id-field-chips'>
                {priority && <Chip size='small' className='chip-small' label={priority.name} sx={{ backgroundColor: priority.color }} />}
                {type && <Chip size='small' className='chip-small' label={type.name} sx={{ backgroundColor: type.color }} />}
                <Box
                    component='button'
                    type='button'
                    onClick={handleOpenActivities}
                    aria-label={translate('resources.device.labels.activities')}
                    sx={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', '&:hover': { textDecoration: 'underline' } }}
                >
                    <CommentBubble count={canSeeHiddenComments ? record.changeCount : record.visibleChangeCount} isSmall={true} />
                </Box>
            </Box>
        </StyledServiceRequestIdField>
    );
};

ServiceRequestIdField.propTypes = {
    record: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        customId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        priority: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        changeCount: PropTypes.number,
        visibleChangeCount: PropTypes.number,
    }),
    resource: PropTypes.string.isRequired,
};
